import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phoneInput';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { FieldError } from '@/components/ui/fieldError';
import { Icon } from '@/components/common/Icon';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { useClientsQuery } from '@/hooks/useClientsQuery';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import { capitalizeWordsAsYouType } from '@/utils/inputFormatting';
import { DuplicateOwnerNotice } from './DuplicateOwnerNotice';
import { validateOwnerDraft, type OwnerDraft, type OwnerMode } from './ownerDraft';

const MODE_LABEL_KEY: Record<OwnerMode, string> = {
  later: 'patients:owner.modeLater',
  existing: 'patients:owner.modeExisting',
  new: 'patients:owner.modeNew',
};

const MODE_HINT_KEY: Record<OwnerMode, string> = {
  later: 'patients:owner.hintLater',
  existing: 'patients:owner.hintExisting',
  new: 'patients:owner.hintNew',
};

interface OwnerSectionProps {
  draft: OwnerDraft;
  modes: OwnerMode[];
  showErrors: boolean;
  onChange: (nextDraft: OwnerDraft) => void;
}

export function OwnerSection({ draft, modes, showErrors, onChange }: OwnerSectionProps) {
  const { t } = useTranslation(['patients', 'clients']);
  const errors = validateOwnerDraft(draft);

  function update(changes: Partial<OwnerDraft>) {
    onChange({ ...draft, ...changes });
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>{t('patients:form.owner')}</Label>
        <div className="flex flex-wrap bg-zoooffwhite border border-zooborder rounded-xl p-1 gap-1">
          {modes.map(function renderMode(mode) {
            const isActive = mode === draft.mode;
            return (
              <button
                key={mode}
                type="button"
                aria-pressed={isActive}
                onClick={function chooseMode() {
                  update({ mode });
                }}
                className={cn(
                  'flex-auto whitespace-nowrap px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-zooprimary text-zoowhite shadow-sm'
                    : 'text-zoomuted hover:text-zooindigo'
                )}
              >
                {t(MODE_LABEL_KEY[mode])}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-zoomuted flex items-start gap-1.5">
          <Icon name="Info" className="text-zooprimary shrink-0 mt-px" />
          {t(MODE_HINT_KEY[draft.mode])}
        </p>
      </div>

      {draft.mode === 'existing' ? (
        <ExistingOwnerField
          draft={draft}
          errorKey={showErrors === true ? errors.existingClient : null}
          onSelect={function selectExisting(existingClientId, existingClientLabel) {
            update({ existingClientId, existingClientLabel });
          }}
        />
      ) : null}

      {draft.mode === 'new' ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="owner-name">{t('patients:owner.fullName')}</Label>
            <Input
              id="owner-name"
              value={draft.fullName}
              hasError={showErrors === true && errors.fullName !== null}
              placeholder={t('clients:form.fullNamePlaceholder')}
              onChange={function changeName(changeEvent) {
                update({ fullName: capitalizeWordsAsYouType(changeEvent.target.value) });
              }}
            />
            <FieldError messageKey={showErrors === true ? errors.fullName : null} />
          </div>
          {modes.includes('existing') ? (
            <DuplicateOwnerNotice
              typedFullName={draft.fullName}
              onUseExistingOwner={function useExistingOwner(client) {
                update({
                  mode: 'existing',
                  existingClientId: client.id,
                  existingClientLabel: client.fullName,
                });
              }}
            />
          ) : null}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="owner-phone">{t('clients:form.phone')}</Label>
              <PhoneInput
                inputId="owner-phone"
                value={draft.phone}
                isValidationVisible={showErrors}
                placeholder={t('clients:form.phonePlaceholder')}
                onChange={function changePhone(phone) {
                  update({ phone });
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="owner-email">{t('clients:form.email')}</Label>
              <Input
                id="owner-email"
                type="email"
                value={draft.email}
                hasError={showErrors === true && errors.email !== null}
                placeholder={t('clients:form.emailPlaceholder')}
                onChange={function changeEmail(changeEvent) {
                  update({ email: changeEvent.target.value.trim() });
                }}
              />
              <FieldError messageKey={showErrors === true ? errors.email : null} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface ExistingOwnerFieldProps {
  draft: OwnerDraft;
  errorKey: string | null;
  onSelect: (clientId: string, label: string) => void;
}

function ExistingOwnerField({ draft, errorKey, onSelect }: ExistingOwnerFieldProps) {
  const { t } = useTranslation('patients');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const clientsQuery = useClientsQuery(1, debouncedSearch, '');
  const options = useMemo(
    function buildOptions() {
      const loadedClients = clientsQuery.data === undefined ? [] : clientsQuery.data.items;
      const loadedOptions = loadedClients.map(function toOption(client) {
        const emailSuffix = client.email === null ? '' : ' · ' + client.email;
        return { value: client.id, label: client.fullName + emailSuffix };
      });
      const selectedIsListed = loadedOptions.some(function matches(option) {
        return option.value === draft.existingClientId;
      });
      if (
        draft.existingClientId.length > 0 &&
        selectedIsListed === false &&
        draft.existingClientLabel.length > 0
      ) {
        return [
          { value: draft.existingClientId, label: draft.existingClientLabel },
          ...loadedOptions,
        ];
      }
      return loadedOptions;
    },
    [clientsQuery.data, draft.existingClientId, draft.existingClientLabel]
  );

  return (
    <div className="space-y-1.5">
      <SearchableSelect
        inputId="patient-owner"
        ariaLabel={t('form.owner')}
        value={draft.existingClientId}
        hasError={errorKey !== null}
        isLoading={clientsQuery.isFetching}
        placeholder={t('form.selectOwner')}
        options={options}
        disableBuiltInFilter
        onInputChange={setSearchInput}
        onChange={function selectOwner(nextValue) {
          const chosen = options.find(function matches(option) {
            return option.value === nextValue;
          });
          onSelect(nextValue, chosen === undefined ? '' : chosen.label);
        }}
      />
      <FieldError messageKey={errorKey} />
    </div>
  );
}
